import { Component, type ErrorInfo, type ReactNode, useEffect, useRef, useState } from "react";
import { gsap } from "../../animations/gsapSetup.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { PRESETS, VERTEX_SHADER } from "./webgl-presets.ts";

// ── Color palettes per route ─────────────────────────────────────────────────

const PALETTES: Record<string, { c1: [number, number, number]; c2: [number, number, number] }> = {
  classic: { c1: [1, 0.42, 0], c2: [1, 0.58, 0.2] },
  ai: { c1: [0, 0.44, 0.89], c2: [0.43, 0.24, 1.0] },
  tennis: { c1: [0.2, 0.78, 0.35], c2: [0.0, 0.55, 0.3] },
  soccer: { c1: [0.15, 0.68, 0.38], c2: [0.9, 0.9, 0.9] },
  golf: { c1: [0.18, 0.56, 0.34], c2: [0.85, 0.72, 0.25] },
};

function getPalette(pathname: string) {
  for (const [key, palette] of Object.entries(PALETTES)) {
    if (pathname.includes(key)) return palette;
  }
  return PALETTES.ai;
}

// ── Raw WebGL helpers ────────────────────────────────────────────────────────

function compileShader(gl: WebGLRenderingContext, src: string, type: number): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.warn("[WebGL] Shader compile error:", gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function buildProgram(gl: WebGLRenderingContext, fsSrc: string): WebGLProgram | null {
  const vs = compileShader(gl, VERTEX_SHADER, gl.VERTEX_SHADER);
  const fs = compileShader(gl, fsSrc, gl.FRAGMENT_SHADER);
  if (!vs || !fs) return null;

  const program = gl.createProgram();
  if (!program) return null;
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.warn("[WebGL] Program link error:", gl.getProgramInfoLog(program));
    gl.deleteProgram(program);
    return null;
  }

  gl.deleteShader(vs);
  gl.deleteShader(fs);
  return program;
}

/** Set up vertex attributes for a program. Call after gl.useProgram(). */
function setupAttributes(gl: WebGLRenderingContext, program: WebGLProgram) {
  const posLoc = gl.getAttribLocation(program, "position");
  const uvLoc = gl.getAttribLocation(program, "uv");
  gl.enableVertexAttribArray(posLoc);
  gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 16, 0);
  if (uvLoc >= 0) {
    gl.enableVertexAttribArray(uvLoc);
    gl.vertexAttribPointer(uvLoc, 2, gl.FLOAT, false, 16, 8);
  }
}

// ── Error Boundary ───────────────────────────────────────────────────────────

class WebGLErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn("[WebGLBackground] Error boundary caught:", error.message, info);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

// ── Canvas component ─────────────────────────────────────────────────────────

function WebGLCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reducedMotion = useReducedMotion();
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const readyRef = useRef(false);

  useEffect(() => {
    // モバイル判定（SPではWebGL負荷を大幅に下げる: DPR=1 + 30fps）
    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (reducedMotion || failed) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Create WebGL context ONCE
    const maybeGl =
      (canvas.getContext("webgl2", {
        alpha: false,
        antialias: false,
        preserveDrawingBuffer: false,
      }) as WebGLRenderingContext | null) ??
      canvas.getContext("webgl", { alpha: false, antialias: false, preserveDrawingBuffer: false });

    if (!maybeGl) {
      console.warn("[WebGLBackground] No WebGL context");
      setFailed(true);
      return;
    }
    // After React StrictMode unmount+remount, the context may be lost
    if (maybeGl.isContextLost()) {
      console.warn("[WebGLBackground] Context is lost (StrictMode remount), skipping");
      return;
    }
    const gl = maybeGl;

    // Shared fullscreen quad buffer
    // biome-ignore format: vertex data alignment
    const vertices = new Float32Array([
      -1,-1, 0,0,
       1,-1, 1,0,
      -1, 1, 0,1,
       1, 1, 1,1,
    ]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    // ── Mutable render state ──
    let currentProgram: WebGLProgram | null = null;
    let uTime: WebGLUniformLocation | null = null;
    let uResolution: WebGLUniformLocation | null = null;
    let uMouse: WebGLUniformLocation | null = null;
    let uScroll: WebGLUniformLocation | null = null;
    let uColor1: WebGLUniformLocation | null = null;
    let uColor2: WebGLUniformLocation | null = null;

    /** Compile a preset and switch to it. Returns true on success. */
    function switchPreset(index: number): boolean {
      const preset = PRESETS[index];
      if (!preset) return false;

      const newProgram = buildProgram(gl, preset.fragment);
      if (!newProgram) {
        console.warn("[WebGL] Failed to build preset:", preset.name);
        return false;
      }

      // Delete old program
      if (currentProgram) gl.deleteProgram(currentProgram);

      currentProgram = newProgram;
      gl.useProgram(currentProgram);

      // Re-bind buffer & attributes for new program
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      setupAttributes(gl, currentProgram);

      // Cache uniform locations
      uTime = gl.getUniformLocation(currentProgram, "u_time");
      uResolution = gl.getUniformLocation(currentProgram, "u_resolution");
      uMouse = gl.getUniformLocation(currentProgram, "u_mouse");
      uScroll = gl.getUniformLocation(currentProgram, "u_scroll");
      uColor1 = gl.getUniformLocation(currentProgram, "u_color1");
      uColor2 = gl.getUniformLocation(currentProgram, "u_color2");

      // Set palette colors
      const palette = getPalette(window.location.pathname);
      gl.uniform3f(uColor1, palette.c1[0], palette.c1[1], palette.c1[2]);
      gl.uniform3f(uColor2, palette.c2[0], palette.c2[1], palette.c2[2]);

      return true;
    }

    // Initial preset
    if (!switchPreset(0)) {
      console.warn("[WebGL] Preset 0 failed, context lost?", gl.isContextLost());
      setFailed(true);
      return;
    }

    // ── Preset switching via custom event ──
    const onPresetChange = ((e: CustomEvent<number>) => {
      switchPreset(e.detail);
    }) as EventListener;
    window.addEventListener("webgl-preset-change", onPresetChange);

    // ── Mouse tracking ──
    const mouseState = { x: 0.5, y: 0.5 };
    const mxTo = gsap.quickTo(mouseState, "x", { duration: 0.6 });
    const myTo = gsap.quickTo(mouseState, "y", { duration: 0.6 });

    const onMouseMove = (e: MouseEvent) => {
      mxTo(e.clientX / window.innerWidth);
      myTo(1 - e.clientY / window.innerHeight);
    };
    window.addEventListener("mousemove", onMouseMove);

    // ── Scroll tracking ──
    const scrollState = { progress: 0 };
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      scrollState.progress = max > 0 ? window.scrollY / max : 0;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    // ── Resize ──
    // SP: DPR=1に制限 (ピクセル数1/4〜1/9でGPU負荷激減)
    const resize = () => {
      const dpr = isTouchDevice ? 1 : Math.min(window.devicePixelRatio, 2);
      const w = canvas.clientWidth * dpr;
      const h = canvas.clientHeight * dpr;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };
    resize();
    window.addEventListener("resize", resize);

    // ── Palette update on SPA navigation ──
    const updatePalette = () => {
      if (!currentProgram) return;
      gl.useProgram(currentProgram);
      const p = getPalette(window.location.pathname);
      gl.uniform3f(uColor1, p.c1[0], p.c1[1], p.c1[2]);
      gl.uniform3f(uColor2, p.c2[0], p.c2[1], p.c2[2]);
    };
    window.addEventListener("popstate", updatePalette);

    // ── Render loop ──
    // SP: フレームスキップで30fps化 (60fps→30fps = 描画負荷半減)
    const startTime = performance.now();
    let rafId: number;
    let paused = false;
    let frameCount = 0;

    const render = () => {
      frameCount++;
      // SP: 偶数フレームのみ描画 (30fps)
      if (!paused && currentProgram && (!isTouchDevice || frameCount % 2 === 0)) {
        try {
          gl.useProgram(currentProgram);
          const elapsed = (performance.now() - startTime) / 1000;
          gl.uniform1f(uTime, elapsed);
          gl.uniform2f(uResolution, canvas.width, canvas.height);
          gl.uniform2f(uMouse, mouseState.x, mouseState.y);
          gl.uniform1f(uScroll, scrollState.progress);
          gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
          if (!readyRef.current) {
            readyRef.current = true;
            setReady(true);
          }
        } catch (err) {
          console.warn("[WebGLBackground] Render error:", err);
          setFailed(true);
          return;
        }
      }
      rafId = requestAnimationFrame(render);
    };
    rafId = requestAnimationFrame(render);

    // Pause when tab hidden
    const onVisibility = () => {
      paused = document.hidden;
    };
    document.addEventListener("visibilitychange", onVisibility);

    // ── Cleanup (only on unmount) ──
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("webgl-preset-change", onPresetChange);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", resize);
      window.removeEventListener("popstate", updatePalette);
      document.removeEventListener("visibilitychange", onVisibility);
      if (currentProgram) gl.deleteProgram(currentProgram);
      if (buffer) gl.deleteBuffer(buffer);
      // NOTE: Do NOT call loseContext() here.
      // React StrictMode re-mounts effects, and a lost context cannot be recovered
      // on the same canvas element, causing all subsequent shader compiles to silently fail.
    };
  }, [reducedMotion, failed]);

  if (reducedMotion || failed) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        zIndex: 1,
        pointerEvents: "none",
        mixBlendMode: "screen",
        opacity: ready ? 1 : 0,
        transition: "opacity 0.6s ease-out",
      }}
    />
  );
}

// ── Export with Error Boundary ───────────────────────────────────────────────

export function WebGLBackground() {
  return (
    <WebGLErrorBoundary>
      <WebGLCanvas />
    </WebGLErrorBoundary>
  );
}
